import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, useFormikContext } from "formik";
import { FormGroup, InputGroupAddon, InputGroupText, InputGroup } from "reactstrap";
import { sanitizeFieldInput } from "../../../helpers/formikForms";
import { useSelector } from "react-redux";
import classnames from "classnames";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as fontAwesome from '@fortawesome/free-solid-svg-icons';

const selectNotificationMessage = (state) => state.notification ?? {};
function CustomField(props) {
  const { message } = useSelector(selectNotificationMessage);
  const { isSubmitting } = useFormikContext();
  const { options, type, showOptionLabel, label } = props;
  const [ displayOptions, setDisplayOptions ] = useState(options);
  const ref = useRef(null);
  const intl = useIntl();

  useEffect(() => {
    if (type === 'select') {
      if (showOptionLabel) {
        if (options) {
          let values = options;
          values.unshift({value: '', display: intl.formatMessage({ id: label }), hidden: true});
          setDisplayOptions(values);
        } else {
          setDisplayOptions([{value: '', display: intl.formatMessage({ id: label }), hidden: true}]);
        }
      }

      if (ref.current && options) {
        let values = [];
        const canvas = document.createElement('canvas');
        let canvasContext = canvas.getContext('2d');

        if (canvasContext) {
          const computedStyle = window.getComputedStyle(ref.current);
          const boundingBox = ref.current.getBoundingClientRect();
          const fontSize = computedStyle.getPropertyValue('font-size');
          const fontFamily = computedStyle.getPropertyValue('font-family');
          const fontWeight = computedStyle.getPropertyValue('font-weight');
          const widthSelect = parseFloat(boundingBox.width);

          if (widthSelect <= 0) {
            return;
          }

          const widthPaddingLeftSelect = parseFloat(computedStyle.getPropertyValue('padding-left'));
          const widthPaddingRightSelect = parseFloat(computedStyle.getPropertyValue('padding-right'));
          const widthBorderLeftSelect = parseFloat(computedStyle.getPropertyValue('border-left-width'));
          const widthBorderRightSelect = parseFloat(computedStyle.getPropertyValue('border-right-width'));
          const maxTextWidth = widthSelect - widthPaddingLeftSelect - widthPaddingRightSelect- widthBorderLeftSelect - widthBorderRightSelect - 40;
          const metricsThreeDots = canvasContext.measureText("...");
          const widthThreeDots = metricsThreeDots.actualBoundingBoxRight + metricsThreeDots.actualBoundingBoxLeft;

          canvasContext.font = fontWeight + ' ' + fontSize + ' ' + fontFamily;

          options.forEach((option) => {
            let displayValue = option.display;
            let metrics = canvasContext.measureText(displayValue);
            let textWidth = metrics.actualBoundingBoxRight + metrics.actualBoundingBoxLeft;

            if (textWidth > maxTextWidth) {
              while(textWidth + widthThreeDots > maxTextWidth) {
                displayValue = displayValue.substring(0, displayValue.length - 1);
                metrics = canvasContext.measureText(displayValue);
                textWidth = metrics.actualBoundingBoxRight + metrics.actualBoundingBoxLeft;
              }

              displayValue = `${displayValue}...`;
            }

            option.display = displayValue;
            values.push(option);
          });

          setDisplayOptions(values);
        }
      }
    }
  }, [options, type, showOptionLabel, label, intl])

  const asProperty = (type) => {
    let fieldType = null;

    if (type === 'select')
      fieldType = 'select';

    if (type === 'textarea')
      fieldType = 'textarea';

    return fieldType;
  };

  const checkIfValid = useCallback(
    (prop) => {
      if (message?.[prop]) {
        if (props.isStaticContactPage) {
          return "contacts-custom-is-invalid";
        }

        return "custom-is-invalid";
      }

      return "";
    },
    [message, props?.isStaticContactPage]
  );

  const handleChange = useCallback(
    (event) => {
      if (typeof props?.onChange === 'function') {
        props.onChange(event);
      }
    },
    [props]
  );

  const handleBlur = useCallback(
    async e => {
      await sanitizeFieldInput(props, !!props?.noWhiteSpaces);

      if (props?.handleBlur) {
        props.handleBlur(e);
      }
    },
    [props]
  );
  const className = classnames(
    "form-control  ",
    props?.classNameField,
    props?.getErrorClass,
    checkIfValid(props?.errorname),
    checkIfValid(props?.name),
    {
      "is-invalid": props?.isInvalid,
      "is-valid": props?.isValid,
      "input-group-merge input-group-alternative": props?.defaultGroupClass,
    });

  return (
    <FormGroup className={props?.className}>
      {
        props?.showLabel ?
        <label
          className='form-control-label'
          htmlFor={'input-' + props?.fieldName}
        >
          <FormattedMessage id={props?.label} />
          <br/>
          {props?.formatDetails && <FormattedMessage id={props?.formatDetails} />}
        </label> : null
      }
      <InputGroup className={`input-group-merge input-group-alternative ${props.errorClass ? props.errorClass : ""}`}>
        {
          props?.fontAwesome ?
          <InputGroupAddon addonType="prepend">
            <InputGroupText>
              <FontAwesomeIcon icon={fontAwesome[props.fontAwesome]} />
            </InputGroupText>
          </InputGroupAddon> : null
        }
        <FormattedMessage id={props?.label} >
          {
            placeholder => (
              <Field
                id={'input-' + props?.fieldName}
                placeholder={placeholder}
                type={props?.type}
                className={className}
                name={props?.fieldName}
                as={asProperty(props?.type)}
                onChange={handleChange}
                value={props?.value ?? ""}
                onBlur={handleBlur }
                checked={props?.type === 'checkbox' ? props?.checked : null}
                disabled={props?.isDisabled || isSubmitting}
                innerRef={ref}
              >
                {
                  displayOptions && displayOptions.length ?
                    displayOptions.map((option) => (
                      <option value={option.value} key={option.value} hidden={option.hidden ? option.hidden : false}>
                        { option.display }
                      </option>
                    )) : null
                }
              </Field>
            )
          }
        </FormattedMessage>

      </InputGroup>
    </FormGroup>
  );
}

CustomField.defaultProps = {
  fieldName: 'firstName',
  label: 'components.pages.profile.edit_profile.first_name_label',
  type: 'text',
  value: '',
  checked: false,
  className: '',
  showLabel: true,
  showOptionLabel: false,
};

export { CustomField };
